import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from '../api/axiosClient';

const LOGO = '/images/logos/Hairchitech_Logo1.png';
const GOLD = '#7d6633';

const StripeSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [checkoutData, setCheckoutData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setError('Invalid session');
      setLoading(false);
      return;
    }

    // Fetch checkout result from backend
    axios.get(`/billing/checkout-result?session_id=${sessionId}`)
      .then((res) => {
        setCheckoutData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching checkout result:', err);
        setError('Failed to load payment details');
        setLoading(false);
      });
  }, [searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-800">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-800">
        <div className="text-white text-xl">{error}</div>
      </div>
    );
  }

  const isNewUser = checkoutData?.isNewUser;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-800">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-r from-amber-900/20 via-yellow-900/10 to-transparent" 
        style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.1) 0%, rgba(125, 102, 51, 0.05) 50%, transparent 100%)' }}>
      </div>

      {/* Abstract Shapes */}
      <div className="absolute top-20 left-10 w-96 h-96 rounded-full blur-3xl" 
        style={{ background: 'radial-gradient(circle, rgba(201, 160, 56, 0.1) 0%, transparent 70%)' }}>
      </div>
      <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full blur-3xl" 
        style={{ background: 'radial-gradient(circle, rgba(201, 160, 56, 0.15) 0%, transparent 70%)' }}>
      </div>
      
      <div className="relative w-full max-w-2xl mx-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-3xl shadow-2xl p-8 md:p-12"
        >
          {/* Success Icon */}
          <div className="flex justify-center mb-6">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{ background: GOLD }}
            >
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </motion.div>
          </div>

          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img src={LOGO} alt="Hairchitech" className="h-16 w-16" />
          </div>

          {/* Heading */}
          <motion.h1 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4"
          >
            {isNewUser ? 'Welcome to Hairchitech!' : 'Subscription Updated!'}
          </motion.h1>

          {/* Message - NEW USER */}
          {isNewUser && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-center mb-8"
            >
              <p className="text-lg text-gray-700 mb-4">
                ✅ Your subscription is active!
              </p>
              <p className="text-gray-600 mb-2">
                We've sent you your login details by email.
              </p>
              <p className="text-sm text-gray-500">
                Check your inbox to access your admin panel and start using Hairchitech!
              </p>
            </motion.div>
          )}

          {/* Message - EXISTING USER */}
          {!isNewUser && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-center mb-8"
            >
              <p className="text-lg text-gray-700 mb-4">
                ✅ Your subscription has been updated successfully!
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-blue-900 font-semibold mb-2">
                  ⚠️ This email is already registered
                </p>
                <p className="text-sm text-blue-700">
                  Please sign in with your existing password to access your admin panel.
                </p>
              </div>
              <p className="text-sm text-gray-600">
                We've also sent you your public booking link — share it on Google Maps, Instagram, and more!
              </p>
            </motion.div>
          )}

          {/* Info Box - NEW USER */}
          {isNewUser && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8"
            >
              <h3 className="font-semibold text-green-900 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                What's Next?
              </h3>
              <ol className="space-y-2 text-sm text-green-800">
                <li className="flex items-start">
                  <span className="font-semibold mr-2">1.</span>
                  <span><strong>Check your email</strong> for your temporary password</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">2.</span>
                  <span><strong>Sign in</strong> to your admin panel</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">3.</span>
                  <span><strong>Change your password</strong> (required on first login)</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">4.</span>
                  <span><strong>Share your booking link</strong> on Google Maps & Instagram</span>
                </li>
              </ol>
            </motion.div>
          )}

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <button
              onClick={() => navigate('/?auth=1')}
              className="px-8 py-4 rounded-xl font-semibold text-white shadow-lg hover:opacity-90 transition-all duration-200"
              style={{ background: GOLD }}
            >
              {isNewUser ? 'Go to Sign In' : 'Sign In Now'}
            </button>
            
            {!isNewUser && (
              <button
                onClick={() => navigate('/forgot-password')}
                className="px-8 py-4 rounded-xl font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-all duration-200 border-2 border-gray-300"
              >
                Forgot Password?
              </button>
            )}
          </motion.div>

          {/* Support Link */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="mt-8 text-center"
          >
            {isNewUser ? (
              <p className="text-sm text-gray-500">
                Didn't receive the email?{' '}
                <a 
                  href="mailto:george.murad00@gmail.com" 
                  className="font-medium hover:opacity-80 transition-opacity"
                  style={{ color: GOLD }}
                >
                  Contact Support
                </a>
              </p>
            ) : (
              <p className="text-sm text-gray-500">
                Need help?{' '}
                <a 
                  href="mailto:george.murad00@gmail.com" 
                  className="font-medium hover:opacity-80 transition-opacity"
                  style={{ color: GOLD }}
                >
                  Contact Support
                </a>
              </p>
            )}
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default StripeSuccessPage;
